import DefaultImage from '../../assets/icons/default-image.svg';
import {encodeUrl, GetFileSize, TruncateTitle} from '../../utils/helper';
import {
    Tooltip,
    TooltipArrow,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '../../ui-components/Tooltip/tooltip';
import {CopyLinkSmall, DeleteIconSmall, FolderDark, HomeLine, ImagePlusBlue} from '../../ui-components/Iconpack';
import {Link} from 'react-router-dom';
import React, {useState} from 'react';
import {FileData} from '../../reducers/media';
import {toast} from 'react-toastify';
import DeleteFile from '../delete-file/deleteFile';
import Breadcrumbs from '../../ui-components/Breadcrumbs/breadcrumbs';
import tracker from '../../lib/analytics/tracker';
import {EventNames} from '../../lib/analytics/events';
import {getCorrectedName} from '../../utils/media';

interface ImageMetadataProps {
    file: FileData;
    backAction: () => void;
    onDeleteImgVersion: (imgId: string) => void;
    orgUid: string;
}

export const ImageVersion: React.FC<ImageMetadataProps> = ({file, backAction, onDeleteImgVersion, orgUid}) => {
    const [showDeleteModal, setShowDeleteModal] = useState<string | null>(null);
    const goToImageDetails = (fileUid: string, versionUid?: string, versionNumber?: number) => {
        if (versionUid && versionNumber) {
            window.location.href =
                window.location.origin +
                `/dashboard/${orgUid}/image-details/${fileUid}?transformedUid=${versionUid}&versionNumber=${versionNumber}`;
        } else {
            window.location.href = window.location.origin + `/dashboard/${orgUid}/image-details/${fileUid}`;
        }
    };

    return (
        <div>
            <div className={'sticky md:hidden'}>
                <Breadcrumbs
                    items={[
                        {
                            label: <HomeLine />,
                            path: '/',
                        },
                        {
                            label: <FolderDark />,
                            path: '/media-library',
                        },
                        {
                            label: file.name,
                        },
                    ]}
                />
            </div>
            <div className='m-4 md:ml-11'>
                <span className='taxt-[#000000] text-[1rem] font-[700]'>Original Image</span>
                <div className='mt-[26px] w-[200px] border-[0.65px] border-solid border-[#D0D5DD] px-[0.472rem] py-3 pt-[0.815rem]'>
                    <div className='mb-4 h-[172px] border-[1px] border-solid border-[#EAECF0] p-3'>
                        <img
                            src={
                                file.thumbnail_url !== ''
                                    ? file.name.toLowerCase().endsWith('.svg') ||
                                      file.name.toLowerCase().endsWith('.gif')
                                        ? encodeUrl(file.cdn_url)
                                        : encodeUrl(file.thumbnail_url)
                                    : DefaultImage
                            }
                            alt={`file-${file.uid}`}
                            className='h-full cursor-pointer items-center justify-center object-contain'
                            onClick={() => goToImageDetails(file.uid)}
                        />
                    </div>
                    <div className='flex flex-col'>
                        <span className='text-[0.875rem] font-[700] text-[#000000]'>
                            {TruncateTitle(file.name, 20)}
                        </span>
                        <span className='pt-1 text-[0.875rem] font-[400] text-[#344054]'>
                            {file.width ? `${file.width}X${file.height}` : 'NA'}
                        </span>
                        <span className='pt-1 text-[0.875rem] font-[700] text-[#344054]'>
                            {GetFileSize(file.file_size)}
                        </span>
                    </div>
                    <div className='flex cursor-pointer items-center justify-between pt-[20px]'>
                        <>
                            <Link to={`${window.location.origin}/dashboard/transformation/${file.uid}`}>
                                <div className='flex items-center gap-[0.5rem]'>
                                    <ImagePlusBlue />
                                    <span className='text-[14px] font-[600] text-[#155EEF]'>Transform</span>
                                </div>
                            </Link>
                        </>
                        <div className='flex justify-end'>
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger>
                                        <div
                                            className='pr-[12px]'
                                            onClick={() => {
                                                navigator.clipboard
                                                    .writeText(encodeUrl(file?.cdn_url) as string)
                                                    .then(() => {
                                                        tracker.trackEvent(EventNames.IMAGE_COPIED);
                                                        toast.success('Image Url Copied!');
                                                    });
                                            }}>
                                            <CopyLinkSmall />
                                        </div>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        <p>Copy Image Url</p>
                                        <TooltipArrow />
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        </div>
                    </div>
                </div>
            </div>
            <div className='ml-1 md:m-11'>
                <span className='taxt-[#000000] m-2 text-[1rem] font-[700]'>Versions</span>
                <div className='mt-3 flex flex-wrap gap-1 md:gap-4'>
                    {Object.entries(file.saved_images).map(([key, imageInfo], index) => (
                        <div
                            key={key}
                            className='w-[180px] border-[0.65px] border-solid  border-[#D0D5DD] px-1 py-3 pt-[0.815rem] md:px-3'>
                            <div className='mb-4 h-[172px] border-[1px] border-solid border-[#EAECF0] p-3'>
                                <img
                                    src={imageInfo.url}
                                    alt={`imageInfo-${imageInfo.uid}`}
                                    className='h-full cursor-pointer items-center justify-center object-contain'
                                    onClick={() => goToImageDetails(file.uid, imageInfo.uid, index + 1)}
                                />
                            </div>
                            <div className='flex flex-col'>
                                <div className='flex justify-between text-[0.875rem] font-[700] text-[#000000]'>
                                    <span>{TruncateTitle(getCorrectedName(imageInfo.title, imageInfo.type), 20)}</span>
                                    <span>#{index + 1}</span>
                                </div>
                                <span className='pt-1 text-[0.875rem] font-[400] text-[#344054]'>
                                    {imageInfo.width ? `${imageInfo.width}X${imageInfo.height}` : 'NA'}
                                </span>
                                <span className='pt-1 text-[0.875rem] font-[700] text-[#344054]'>
                                    {GetFileSize(imageInfo.size)}
                                </span>
                            </div>
                            <div className='flex cursor-pointer items-center justify-between pt-[20px]'>
                                <>
                                    <Link
                                        to={`${window.location.origin}/dashboard/transformation/${file.uid}?transformId=${imageInfo.uid}`}>
                                        <div className='flex items-center gap-[0.5rem]'>
                                            <ImagePlusBlue />
                                            <span className='text-[14px] font-[600] text-[#155EEF]'>Transform</span>
                                        </div>
                                    </Link>
                                </>
                                <div className='flex justify-end'>
                                    <TooltipProvider>
                                        <Tooltip>
                                            <TooltipTrigger>
                                                <div
                                                    className='pr-[12px]'
                                                    onClick={() => {
                                                        navigator.clipboard.writeText(imageInfo.url).then(() => {
                                                            tracker.trackEvent(EventNames.TRANSFORMATION_COPIED);
                                                            toast.success('Image Url Copied!');
                                                        });
                                                    }}>
                                                    <CopyLinkSmall />
                                                </div>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                <p>Copy Image Url</p>
                                                <TooltipArrow />
                                            </TooltipContent>
                                        </Tooltip>
                                    </TooltipProvider>
                                    <DeleteFile
                                        fileName={imageInfo.title}
                                        onClose={() => setShowDeleteModal(null)}
                                        onConfirm={() => {
                                            setShowDeleteModal(null);
                                            onDeleteImgVersion(imageInfo.uid);
                                        }}
                                        open={showDeleteModal === imageInfo.uid}
                                    />
                                    <div
                                        onClick={() => {
                                            setShowDeleteModal(imageInfo.uid);
                                        }}>
                                        <DeleteIconSmall />
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};
export default ImageVersion;
